import { STAFF_FEATURES, STAFF_PERMISSIONS_ROLE_TYPES, resolveAuthorityLevelsFromSchool } from '../constants';
import type { SchoolSettings } from '../types';

export type StaffPermissionsMode = 'ROLE' | 'AUTHORITY';

export interface StaffFeaturePermissionBlock {
  id: string;
  selectedKeys: string[];
  features: Record<string, { view: boolean; edit: boolean }>;
  studentScope: string;
}

function newId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `b_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function principalOrAdminRole(r: string): boolean {
  return r === 'PRINCIPAL' || r === 'ADMIN';
}

function resolvedLevels(school?: SchoolSettings) {
  return resolveAuthorityLevelsFromSchool(school?.authorityLevels);
}

/** Defaults for a card from its selection (matches legacy role defaults). */
export function defaultFeatureMatrixForBlock(
  mode: StaffPermissionsMode,
  selectedKeys: string[],
  schoolSettings?: SchoolSettings,
): Record<string, { view: boolean; edit: boolean }> {
  const levels = resolvedLevels(schoolSettings);
  const topValue = levels[0]?.value;

  let principalLike = false;
  if (mode === 'ROLE') {
    principalLike = selectedKeys.some(principalOrAdminRole);
  } else {
    principalLike = Boolean(topValue && selectedKeys.includes(topValue));
  }

  const teacherLike =
    mode === 'ROLE'
      ? selectedKeys.includes('TEACHER')
      : selectedKeys.some((k) => k && k !== topValue);

  const defaultViewTeacher = ['dashboard', 'dispersals', 'students', 'notifications', 'meetings'];
  const defaultEditTeacher = ['dispersals'];
  const out: Record<string, { view: boolean; edit: boolean }> = {};
  STAFF_FEATURES.forEach((f) => {
    let view = false;
    let edit = false;
    if (principalLike) {
      view = true;
      edit = !f.viewOnly;
    } else if (teacherLike) {
      view = defaultViewTeacher.includes(f.id);
      edit = defaultEditTeacher.includes(f.id);
    }
    out[f.id] = { view, edit };
  });
  return out;
}

function featureMatrixFromRaw(
  raw: Record<string, { view?: boolean; edit?: boolean }> | undefined,
  mode: StaffPermissionsMode,
  selectedKeys: string[],
  schoolSettings?: SchoolSettings,
): Record<string, { view: boolean; edit: boolean }> {
  const base = defaultFeatureMatrixForBlock(mode, selectedKeys, schoolSettings);
  if (!raw) return base;
  STAFF_FEATURES.forEach((f) => {
    const cur = raw[f.id];
    if (cur) {
      base[f.id] = {
        view: cur.view !== undefined ? !!cur.view : base[f.id].view,
        edit: cur.edit !== undefined ? !!cur.edit : base[f.id].edit,
      };
    }
  });
  return base;
}

function defaultStudentScopeForRole(role: string): string {
  if (role === 'TEACHER') return 'CLASS_ONLY';
  if (principalOrAdminRole(role)) return 'ALL';
  return 'NONE';
}

const SCOPE_RANK: Record<string, number> = {
  NONE: 0,
  ASSIGNED_ONLY: 1,
  CLASS_ONLY: 2,
  ALL: 3,
};

/** Most restrictive student scope across keys (for single-card legacy migration). */
function narrowestStudentScope(keys: string[], accessByKey: Record<string, string>): string {
  let worst = 'ALL';
  keys.forEach((k) => {
    const s = accessByKey[k] || 'NONE';
    if ((SCOPE_RANK[s] ?? 0) < (SCOPE_RANK[worst] ?? 99)) worst = s;
  });
  return worst;
}

/** One card: union (OR) of feature flags across keys — legacy migration so no one loses access; tighten in UI. */
function unionMergedFeatures(
  mode: StaffPermissionsMode,
  keys: string[],
  getRaw: (key: string) => Record<string, { view?: boolean; edit?: boolean }> | undefined,
  schoolSettings?: SchoolSettings,
): Record<string, { view: boolean; edit: boolean }> {
  const base = defaultFeatureMatrixForBlock(mode, keys, schoolSettings);
  if (!keys.length) return base;
  STAFF_FEATURES.forEach((f) => {
    let view = false;
    let edit = false;
    keys.forEach((k) => {
      const m = getRaw(k)?.[f.id];
      if (m) {
        view = view || !!m.view;
        edit = edit || !!m.edit;
      }
    });
    base[f.id] = { view, edit };
  });
  return base;
}

export function emptyBlock(
  mode: StaffPermissionsMode,
  school: { settings?: SchoolSettings },
): StaffFeaturePermissionBlock {
  return {
    id: newId(),
    selectedKeys: [],
    features: defaultFeatureMatrixForBlock(mode, [], school.settings),
    studentScope: 'NONE',
  };
}

/** Load editor state from school (blocks + mode + student access map for role mode). */
export function loadEditorFromSchool(school: {
  settings?: SchoolSettings;
}): {
  mode: StaffPermissionsMode;
  blocks: StaffFeaturePermissionBlock[];
  roleStudentAccess: Record<string, string>;
} {
  const mode: StaffPermissionsMode =
    school.settings?.staffFeaturePermissionsMode === 'AUTHORITY' ? 'AUTHORITY' : 'ROLE';
  const rawBlocks = school.settings?.staffFeaturePermissionBlocks;

  const staffAccessFromSchool = school.settings?.staffStudentAccess || {};
  const roleStudentAccess: Record<string, string> = {};
  STAFF_PERMISSIONS_ROLE_TYPES.forEach(({ value: r }) => {
    roleStudentAccess[r] = staffAccessFromSchool[r] || defaultStudentScopeForRole(r);
  });

  if (Array.isArray(rawBlocks) && rawBlocks.length > 0) {
    const blocks: StaffFeaturePermissionBlock[] = rawBlocks
      .filter((b) => b && typeof b === 'object')
      .map((b) => {
        const id = typeof (b as { id?: string }).id === 'string' ? (b as { id: string }).id : newId();
        const selectedKeys = Array.isArray((b as { selectedKeys?: unknown }).selectedKeys)
          ? (b as { selectedKeys: string[] }).selectedKeys.map(String)
          : [];
        const rawF = (b as { features?: Record<string, { view?: boolean; edit?: boolean }> }).features;
        const features = featureMatrixFromRaw(rawF, mode, selectedKeys, school.settings);
        const studentScope =
          typeof (b as { studentScope?: string }).studentScope === 'string'
            ? String((b as { studentScope: string }).studentScope)
            : 'NONE';
        return { id, selectedKeys, features, studentScope };
      });
    return { mode, blocks, roleStudentAccess };
  }

  const levels = resolvedLevels(school.settings);
  const levelValues = levels.map((l) => l.value);

  if (mode === 'AUTHORITY') {
    const byF = school.settings?.staffFeatureAccessByAuthority || {};
    const byS = school.settings?.staffStudentAccessByAuthority || {};
    const hasLegacy = levelValues.some((lv) => byF[lv] && Object.keys(byF[lv]).length > 0);
    if (hasLegacy && levelValues.length) {
      const features = unionMergedFeatures(
        'AUTHORITY',
        levelValues,
        (k) => byF[k],
        school.settings,
      );
      const studentScope = narrowestStudentScope(levelValues, byS);
      return {
        mode,
        blocks: [{ id: newId(), selectedKeys: levelValues, features, studentScope }],
        roleStudentAccess,
      };
    }
    return {
      mode,
      blocks: [emptyBlock('AUTHORITY', school)],
      roleStudentAccess,
    };
  }

  const rawFeature = school.settings?.staffFeatureAccess || {};
  const roleKeys = STAFF_PERMISSIONS_ROLE_TYPES.map(({ value }) => value);
  const hasLegacy = roleKeys.some((rk) => rawFeature[rk] && Object.keys(rawFeature[rk]).length > 0);
  if (hasLegacy) {
    const accessMap: Record<string, string> = {};
    roleKeys.forEach((rk) => {
      accessMap[rk] = roleStudentAccess[rk] || defaultStudentScopeForRole(rk);
    });
    const features = unionMergedFeatures('ROLE', roleKeys, (k) => rawFeature[k], school.settings);
    const studentScope = narrowestStudentScope(roleKeys, accessMap);
    return {
      mode,
      blocks: [{ id: newId(), selectedKeys: roleKeys, features, studentScope }],
      roleStudentAccess,
    };
  }

  return {
    mode,
    blocks: [emptyBlock('ROLE', school)],
    roleStudentAccess,
  };
}

/** Default blocks after mode switch (reset) — one empty card; other mode is cleared on next Save. */
export function defaultBlocksAfterModeSwitch(
  mode: StaffPermissionsMode,
  school: { settings?: SchoolSettings },
): StaffFeaturePermissionBlock[] {
  return [emptyBlock(mode, school)];
}

/** Apply blocks to flat maps for API / other screens. */
export function flattenBlocksToSettings(
  blocks: StaffFeaturePermissionBlock[],
  mode: StaffPermissionsMode,
): Pick<
  SchoolSettings,
  | 'staffFeatureAccess'
  | 'staffStudentAccess'
  | 'staffFeatureAccessByAuthority'
  | 'staffStudentAccessByAuthority'
> {
  const staffFeatureAccess: Record<string, Record<string, { view: boolean; edit: boolean }>> = {};
  const staffStudentAccess: Record<string, string> = {};
  const staffFeatureAccessByAuthority: Record<string, Record<string, { view: boolean; edit: boolean }>> = {};
  const staffStudentAccessByAuthority: Record<string, string> = {};

  blocks.forEach((block) => {
    block.selectedKeys.forEach((key) => {
      if (mode === 'ROLE') {
        staffFeatureAccess[key] = { ...block.features };
        staffStudentAccess[key] = block.studentScope;
      } else {
        staffFeatureAccessByAuthority[key] = { ...block.features };
        staffStudentAccessByAuthority[key] = block.studentScope;
      }
    });
  });

  return {
    staffFeatureAccess,
    staffStudentAccess,
    staffFeatureAccessByAuthority,
    staffStudentAccessByAuthority,
  };
}

/**
 * Persisted settings for Staff Permissions save: only the active mode’s maps are kept;
 * the other mode’s maps are cleared so role-based and authority-based rules never apply together.
 */
export function buildStaffPermissionsSaveSettings(
  school: { settings?: SchoolSettings },
  mode: StaffPermissionsMode,
  blocks: StaffFeaturePermissionBlock[],
): SchoolSettings {
  const flat = flattenBlocksToSettings(blocks, mode);
  const prev = school.settings || {};
  if (mode === 'ROLE') {
    return {
      ...prev,
      staffFeaturePermissionsMode: mode,
      staffFeaturePermissionBlocks: blocks,
      staffFeatureAccess: { ...(prev.staffFeatureAccess || {}), ...flat.staffFeatureAccess },
      staffStudentAccess: { ...(prev.staffStudentAccess || {}), ...flat.staffStudentAccess },
      staffFeatureAccessByAuthority: {},
      staffStudentAccessByAuthority: {},
    };
  }
  return {
    ...prev,
    staffFeaturePermissionsMode: mode,
    staffFeaturePermissionBlocks: blocks,
    staffFeatureAccessByAuthority: { ...flat.staffFeatureAccessByAuthority },
    staffStudentAccessByAuthority: { ...flat.staffStudentAccessByAuthority },
    staffFeatureAccess: {},
    staffStudentAccess: {},
  };
}

/** Keep existing toggles when selection changes; fill missing keys from defaults. */
export function mergeFeaturesForSelection(
  existing: Record<string, { view: boolean; edit: boolean }>,
  mode: StaffPermissionsMode,
  selectedKeys: string[],
  schoolSettings?: SchoolSettings,
): Record<string, { view: boolean; edit: boolean }> {
  return featureMatrixFromRaw(existing, mode, selectedKeys, schoolSettings);
}

function deepestLadderKeyWithPolicy(
  levels: { value: string }[],
  map: Record<string, Record<string, unknown>> | undefined,
): string | undefined {
  if (!levels.length || !map) return undefined;
  for (let i = levels.length - 1; i >= 0; i -= 1) {
    const v = levels[i].value;
    const row = map[v];
    if (row && typeof row === 'object' && Object.keys(row).length > 0) return v;
  }
  return undefined;
}

/** Resolve which key to use in the active feature map for the current user (client). */
export function getFeatureAccessLookupKey(
  user: { role?: string; authorityLevel?: string; roleType?: string } | null,
  school: { settings?: SchoolSettings } | undefined,
): string | undefined {
  if (!user) return undefined;
  const mode = school?.settings?.staffFeaturePermissionsMode === 'AUTHORITY' ? 'AUTHORITY' : 'ROLE';
  if (mode === 'AUTHORITY') {
    const levels = resolvedLevels(school?.settings);
    const map = (school?.settings?.staffFeatureAccessByAuthority || {}) as Record<
      string,
      Record<string, unknown>
    >;
    const top = levels[0]?.value ?? 'LEVEL_1';
    const bottom = levels.length ? levels[levels.length - 1].value : 'LEVEL_5';
    if (user.role === 'PRINCIPAL' || user.role === 'ADMIN') return top;
    const raw = user.authorityLevel?.trim();
    if (raw) {
      const matched = levels.find(
        (l) => l.value === raw || l.value.toLowerCase() === raw.toLowerCase(),
      );
      if (matched) return matched.value;
      const mapKeys = Object.keys(map || {});
      const keyHit = mapKeys.find((k) => k === raw || k.toLowerCase() === raw.toLowerCase());
      if (keyHit) return keyHit;
    }
    const fromPolicy = deepestLadderKeyWithPolicy(levels, map);
    if (fromPolicy) return fromPolicy;
    return bottom;
  }
  return (user.roleType || user.role) as string | undefined;
}
